import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, Pencil, Trash2, Plus, Calendar, TrendingUp, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import CompanyDialog from '../components/CompanyDialog';
import ContactDialog from '../components/ContactDialog';
import ContactCard from '../components/ContactCard';
import OpportunityDialog from '../components/OpportunityDialog';
import ActivityDialog from '../components/ActivityDialog';
import TaskDialog from '../components/TaskDialog';
import DeleteDialog from '../components/DeleteDialog';
import OpportunityViewDialog from '../components/OpportunityViewDialog';
import AccountHistorySection from '../components/AccountHistorySection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tooltips for Estado (commercial relationship)
const STATUS_TOOLTIPS = {
  'Prospecto': 'Empresa identificada con potencial. No existe aún una relación comercial consolidada.',
  'En relación': 'Existe contacto activo y vínculo comercial en desarrollo.',
  'Cliente': 'Empresa con contrato vigente o relación comercial reciente.',
  'No estratégica': 'Empresa que actualmente no se prioriza o no encaja estratégicamente.'
};

// Tooltips for Rol (strategic positioning)
const ROL_TOOLTIPS = {
  'Cliente': 'Entidad que puede contratarnos directa o indirectamente.',
  'Partner': 'Empresa con la que colaboramos o podemos colaborar estratégicamente.',
  'Competidor': 'Empresa que compite directamente con nuestros servicios principales.',
  'Mixto': 'Empresa que en algunos proyectos compite y en otros colabora con nosotros.'
};

// Status badge colors (commercial relationship)
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'Prospecto': return 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200';
    case 'En relación': return 'bg-sky-100 text-sky-800 border-sky-300 hover:bg-sky-200';
    case 'Cliente': return 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200';
    case 'No estratégica': return 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

// Rol badge colors (strategic positioning)
const getRolBadgeClass = (rol) => {
  switch (rol) {
    case 'Cliente': return 'bg-teal-100 text-teal-800 border-teal-300 hover:bg-teal-200';
    case 'Partner': return 'bg-violet-100 text-violet-800 border-violet-300 hover:bg-violet-200';
    case 'Competidor': return 'bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-200';
    case 'Mixto': return 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

// Stage colors for opportunity badges
const stageColors = {
  'Detectada': 'bg-blue-100 text-blue-800',
  'Propuesta': 'bg-yellow-100 text-yellow-800',
  'Presentada': 'bg-purple-100 text-purple-800',
  'Ganada': 'bg-green-100 text-green-800',
  'Perdida': 'bg-red-100 text-red-800',
  'Descartada': 'bg-gray-100 text-gray-800',
  // Legacy stages
  'Lead': 'bg-blue-100 text-blue-800',
  'Qualified': 'bg-yellow-100 text-yellow-800',
  'Proposal': 'bg-purple-100 text-purple-800',
  'Negotiation': 'bg-orange-100 text-orange-800',
  'Won': 'bg-green-100 text-green-800',
  'Lost': 'bg-red-100 text-red-800',
};

const relevanceBadgeColors = {
  'Alta': 'bg-red-100 text-red-700',
  'Media': 'bg-yellow-100 text-yellow-700',
  'Baja': 'bg-green-100 text-green-700',
};

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor } = useUser();
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [opportunityDialogOpen, setOpportunityDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Delete confirmation states
  const [deleteContactConfirm, setDeleteContactConfirm] = useState(null);
  const [deleteOpportunityConfirm, setDeleteOpportunityConfirm] = useState(null);
  const [deleteActivityConfirm, setDeleteActivityConfirm] = useState(null);
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState(null);
  
  // View dialog state
  const [viewingOpportunity, setViewingOpportunity] = useState(null);
  
  // TYPSA contacts grouping
  const [expandedContactGroups, setExpandedContactGroups] = useState({});

  useEffect(() => {
    fetchCompanyData();
  }, [id]);

  const fetchCompanyData = async () => {
    try {
      const [companyRes, contactsRes, oppsRes, activitiesRes, tasksRes] = await Promise.all([
        axios.get(`${API}/companies/${id}`),
        axios.get(`${API}/contacts?company_id=${id}`),
        axios.get(`${API}/opportunities?company_id=${id}`),
        axios.get(`${API}/activities?company_id=${id}`),
        axios.get(`${API}/tasks?company_id=${id}`)
      ]);
      setCompany(companyRes.data);
      setContacts(contactsRes.data);
      setOpportunities(oppsRes.data);
      setActivities(activitiesRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching company data:', error);
      toast.error('Error al cargar los datos de la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success('Empresa eliminada correctamente');
      navigate('/companies');
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Error al eliminar la empresa');
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteContactConfirm) return;
    try {
      await axios.delete(`${API}/contacts/${deleteContactConfirm.id}`);
      toast.success('Contacto eliminado');
      fetchCompanyData();
    } catch (error) {
      toast.error('Error al eliminar contacto');
    } finally {
      setDeleteContactConfirm(null);
    }
  };

  const handleDeleteOpportunity = async () => {
    if (!deleteOpportunityConfirm) return;
    try {
      await axios.delete(`${API}/opportunities/${deleteOpportunityConfirm.id}`);
      toast.success('Oportunidad eliminada');
      fetchCompanyData();
    } catch (error) {
      toast.error('Error al eliminar oportunidad');
    } finally {
      setDeleteOpportunityConfirm(null);
    }
  };

  const handleDeleteActivity = async () => {
    if (!deleteActivityConfirm) return;
    try {
      await axios.delete(`${API}/activities/${deleteActivityConfirm.id}`);
      toast.success('Actividad eliminada');
      fetchCompanyData();
    } catch (error) {
      toast.error('Error al eliminar actividad');
    } finally {
      setDeleteActivityConfirm(null);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskConfirm) return;
    try {
      await axios.delete(`${API}/tasks/${deleteTaskConfirm.id}`);
      toast.success('Tarea eliminada');
      fetchCompanyData();
    } catch (error) {
      toast.error('Error al eliminar tarea');
    } finally {
      setDeleteTaskConfirm(null);
    }
  };

  // Check if this is TYPSA
  const isTypsa = company?.name?.toUpperCase().includes('TYPSA');

  // Toggle contact group expansion
  const toggleContactGroup = (groupKey) => {
    setExpandedContactGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const labels = {
      'division': 'División',
      'departamento': 'Departamento',
      'territorio': 'Territorio',
      'grupo': 'Grupo'
    };
    return labels[category] || category;
  };

  // Group contacts by TYPSA category
  const getGroupedContacts = () => {
    if (!isTypsa) {
      return [{ key: 'all', label: null, contacts: contacts }];
    }

    const groups = {};
    const ungrouped = [];

    contacts.forEach(contact => {
      if (contact.typsa_category && contact.typsa_category_value) {
        const groupKey = `${contact.typsa_category}-${contact.typsa_category_value}`;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            category: contact.typsa_category,
            value: contact.typsa_category_value,
            contacts: []
          };
        }
        groups[groupKey].contacts.push(contact);
      } else {
        ungrouped.push(contact);
      }
    });

    // Sort groups by category then value
    const sortedGroups = Object.entries(groups)
      .sort(([, a], [, b]) => {
        const catOrder = ['division', 'departamento', 'territorio', 'grupo'];
        const catA = catOrder.indexOf(a.category);
        const catB = catOrder.indexOf(b.category);
        if (catA !== catB) return catA - catB;
        return a.value.localeCompare(b.value, 'es');
      })
      .map(([key, group]) => ({
        key,
        label: `${getCategoryLabel(group.category)}: ${group.value}`,
        category: group.category,
        contacts: group.contacts
      }));

    // Add ungrouped contacts at the end
    if (ungrouped.length > 0) {
      sortedGroups.push({
        key: 'sin-clasificar',
        label: 'Sin clasificar',
        category: null,
        contacts: ungrouped
      });
    }

    return sortedGroups;
  };

  const groupedContacts = getGroupedContacts();

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-sm text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-8">
        <div className="text-sm text-muted-foreground">Empresa no encontrada</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="company-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/companies')}
        className="mb-4"
        data-testid="back-to-companies-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Empresas
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 md:mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2 break-words" data-testid="company-name">{company.name}</h1>
          
          {/* Estado and Rol badges with tooltips */}
          <TooltipProvider delayDuration={200}>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {/* Estado badge - Relación comercial */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Estado:</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span 
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border cursor-help transition-colors ${getStatusBadgeClass(company.status)}`}
                      data-testid="company-detail-status"
                    >
                      {company.status || 'Sin definir'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">{STATUS_TOOLTIPS[company.status] || 'Estado de la relación comercial'}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              
              {/* Rol badge - Posicionamiento estratégico */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rol:</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span 
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border cursor-help transition-colors ${getRolBadgeClass(company.rol)}`}
                      data-testid="company-detail-rol"
                    >
                      {company.rol || 'Sin definir'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">{ROL_TOOLTIPS[company.rol] || 'Rol estratégico de la empresa'}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
          
          {/* Company metadata */}
          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-muted-foreground">
            {company.sector && <span>{company.sector}</span>}
            {company.city && company.country && (
              <span>
                {company.city}, {company.country}
              </span>
            )}
            {company.company_type && (
              <Badge variant="outline" className="text-xs">
                {company.company_type === 'publica' ? 'Pública' : 
                 company.company_type === 'privada' ? 'Privada' : 
                 company.company_type}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {isEditor && (
            <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)} data-testid="edit-company-btn">
              <Pencil className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Editar</span>
            </Button>
          )}
          {isEditor && (
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-company-btn">
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Eliminar</span>
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4 md:space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-max md:w-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs md:text-sm">General</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="text-xs md:text-sm">Contactos ({contacts.length})</TabsTrigger>
            <TabsTrigger value="opportunities" data-testid="tab-opportunities" className="text-xs md:text-sm">Oport. ({opportunities.length})</TabsTrigger>
            <TabsTrigger value="activities" data-testid="tab-activities" className="text-xs md:text-sm">Actividades ({activities.length})</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks" className="text-xs md:text-sm">Tareas ({tasks.length})</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg md:text-xl font-medium tracking-tight">Información de la Empresa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Sitio Web</div>
                  <div className="break-all">{company.website || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Responsable</div>
                  <div>{company.owner || 'N/A'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Account History Section - replaces old Notes field */}
          <AccountHistorySection companyId={company.id} />
        </TabsContent>

        <TabsContent value="contacts">
          <div className="space-y-4">
            {isEditor && (
              <div className="flex justify-end">
                <Button onClick={() => setContactDialogOpen(true)} data-testid="add-contact-btn" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Contacto
                </Button>
              </div>
            )}
            {contacts.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  No hay contactos
                </CardContent>
              </Card>
            ) : isTypsa ? (
              /* TYPSA: Grouped contacts view */
              <div className="space-y-3">
                {groupedContacts.map((group) => {
                  const isExpanded = expandedContactGroups[group.key] ?? false;
                  
                  return (
                    <div key={group.key} className="space-y-2">
                      {/* Group Header */}
                      <div
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => toggleContactGroup(group.key)}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          )}
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{group.label}</span>
                        </div>
                        <Badge variant="secondary">{group.contacts.length}</Badge>
                      </div>
                      
                      {/* Contacts in Group */}
                      {isExpanded && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pl-4">
                          {group.contacts.map((contact) => (
                            <ContactCard
                              key={contact.id}
                              contact={contact}
                              companies={[company]}
                              typsaOrgUnits={[]}
                              isEditor={isEditor}
                              onEdit={(c) => {
                                setEditingContact(c);
                                setContactDialogOpen(true);
                              }}
                              onDelete={(c) => setDeleteContactConfirm(c)}
                              showCompanyName={false}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Normal contacts view */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {contacts.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    companies={[company]}
                    typsaOrgUnits={[]}
                    isEditor={isEditor}
                    onEdit={(c) => {
                      setEditingContact(c);
                      setContactDialogOpen(true);
                    }}
                    onDelete={(c) => setDeleteContactConfirm(c)}
                    showCompanyName={false}
                  />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="opportunities">
          <div className="space-y-4">
            {isEditor && (
              <div className="flex justify-end">
                <Button onClick={() => setOpportunityDialogOpen(true)} data-testid="add-opportunity-btn" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Oportunidad
                </Button>
              </div>
            )}
            {opportunities.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  No hay oportunidades
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {opportunities.map((opp) => {
                  const title = opp.title || opp.name || 'Sin título';
                  const stage = opp.stage || 'Detectada';
                  const deliveryDate = opp.expected_delivery_date || opp.expected_close_date;
                  
                  return (
                    <Card 
                      key={opp.id} 
                      data-testid={`opportunity-${opp.id}`}
                      className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-primary/50"
                      onClick={() => setViewingOpportunity(opp)}
                    >
                      <CardContent className="py-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">{title}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
                              <Badge className={stageColors[stage] || 'bg-gray-100'}>{stage}</Badge>
                              {/* Show offered_amount if exists, otherwise estimated_value */}
                              {(opp.offered_amount || opp.estimated_value) && (
                                <span className="font-semibold text-green-600">
                                  €{(opp.offered_amount || opp.estimated_value).toLocaleString('es-ES')}
                                  {opp.offered_amount && (
                                    <span className="text-xs font-normal text-muted-foreground ml-1">(ofertado)</span>
                                  )}
                                </span>
                              )}
                            </div>
                            {deliveryDate && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                <Calendar className="w-3 h-3" />
                                <span>{deliveryDate}</span>
                              </div>
                            )}
                            {/* Relevance and Probability badges */}
                            {(opp.relevance || opp.probability) && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {opp.relevance && (
                                  <Badge className={`text-xs ${relevanceBadgeColors[opp.relevance] || 'bg-gray-100'}`}>
                                    {opp.relevance}
                                  </Badge>
                                )}
                                {opp.probability && (
                                  <Badge variant="outline" className="text-xs">
                                    <TrendingUp className="w-3 h-3 mr-1" />
                                    {opp.probability}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            {isEditor && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingOpportunity(opp);
                                  setOpportunityDialogOpen(true);
                                }}
                                data-testid={`edit-opportunity-${opp.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {isEditor && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteOpportunityConfirm(opp)}
                                data-testid={`delete-opportunity-${opp.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activities">
          <div className="space-y-4">
            {isEditor && (
              <div className="flex justify-end">
                <Button onClick={() => setActivityDialogOpen(true)} data-testid="add-activity-btn" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Actividad
                </Button>
              </div>
            )}
            {activities.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  No hay actividades
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {activities.map((activity) => (
                  <Card key={activity.id} data-testid={`activity-${activity.id}`}>
                    <CardContent className="py-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{activity.type}</Badge>
                            <span className="text-sm text-muted-foreground">{activity.date}</span>
                          </div>
                          {activity.notes && <div className="text-sm mt-2">{activity.notes}</div>}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {isEditor && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingActivity(activity);
                                setActivityDialogOpen(true);
                              }}
                              data-testid={`edit-activity-${activity.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {isEditor && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteActivityConfirm(activity)}
                              data-testid={`delete-activity-${activity.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="space-y-4">
            {isEditor && (
              <div className="flex justify-end">
                <Button onClick={() => setTaskDialogOpen(true)} data-testid="add-task-btn" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Tarea
                </Button>
              </div>
            )}
            {tasks.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  No hay tareas
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <Card key={task.id} data-testid={`task-${task.id}`}>
                    <CardContent className="py-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{task.title}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
                            <Badge variant={task.status === 'Done' ? 'default' : 'secondary'}>{task.status}</Badge>
                            {task.due_date && (
                              <span className="text-muted-foreground text-xs">Vence: {task.due_date}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {isEditor && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingTask(task);
                                setTaskDialogOpen(true);
                              }}
                              data-testid={`edit-task-${task.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {isEditor && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteTaskConfirm(task)}
                              data-testid={`delete-task-${task.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <CompanyDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        company={company}
        onSuccess={fetchCompanyData}
      />
      <ContactDialog
        open={contactDialogOpen}
        onOpenChange={(open) => {
          setContactDialogOpen(open);
          if (!open) setEditingContact(null);
        }}
        companyId={id}
        contact={editingContact}
        onSuccess={fetchCompanyData}
      />
      <OpportunityDialog
        open={opportunityDialogOpen}
        onOpenChange={(open) => {
          setOpportunityDialogOpen(open);
          if (!open) setEditingOpportunity(null);
        }}
        companyId={id}
        opportunity={editingOpportunity}
        onSuccess={fetchCompanyData}
      />
      <ActivityDialog
        open={activityDialogOpen}
        onOpenChange={(open) => {
          setActivityDialogOpen(open);
          if (!open) setEditingActivity(null);
        }}
        companyId={id}
        activity={editingActivity}
        onSuccess={fetchCompanyData}
      />
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) setEditingTask(null);
        }}
        companyId={id}
        task={editingTask}
        onSuccess={fetchCompanyData}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Company"
        description="Are you sure you want to delete this company? This action cannot be undone."
      />

      {/* Delete Contact Confirmation */}
      <AlertDialog open={!!deleteContactConfirm} onOpenChange={(open) => !open && setDeleteContactConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Contacto</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar a "{deleteContactConfirm?.name}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContact} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Opportunity Confirmation */}
      <AlertDialog open={!!deleteOpportunityConfirm} onOpenChange={(open) => !open && setDeleteOpportunityConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Oportunidad</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar "{deleteOpportunityConfirm?.title || deleteOpportunityConfirm?.name}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOpportunity} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Activity Confirmation */}
      <AlertDialog open={!!deleteActivityConfirm} onOpenChange={(open) => !open && setDeleteActivityConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Actividad</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar esta actividad ({deleteActivityConfirm?.type} - {deleteActivityConfirm?.date})? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteActivity} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Task Confirmation */}
      <AlertDialog open={!!deleteTaskConfirm} onOpenChange={(open) => !open && setDeleteTaskConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Tarea</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar "{deleteTaskConfirm?.title}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTask} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Opportunity View Dialog */}
      <OpportunityViewDialog
        open={!!viewingOpportunity}
        onOpenChange={(open) => !open && setViewingOpportunity(null)}
        opportunity={viewingOpportunity}
        companyName={company?.name}
      />
    </div>
  );
};

export default CompanyDetail;